import { useState, useMemo } from 'react';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PaymentsIcon from '@mui/icons-material/Payments';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { MdOutlineSystemUpdateAlt } from 'react-icons/md';
import { orderBy as lodashOrderBy } from 'lodash';

import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import Scrollbar from '../../../components/Scrollbar';
import SearchNotFound from '../../../components/SearchNotFound';
import SimplePagination from '../../../components/SimplePagination';
import { UserListHead } from '../../../components/_dashboard/user';
import Label from '../../../components/Label';
import { RootStyle, SearchStyle, StyledTableRow, StyledTableCell } from '../../Global/Styling';

// ── Table columns ──────────────────────────────────────────────────────
const TABLE_HEAD = [
  { id: 'period', label: 'Period' },
  { id: 'section', label: 'Section' },
  { id: 'paymentAmt', label: 'Payment Amt' },
  { id: 'tdsRate', label: 'TDS Rate' },
  { id: 'tdsDeducted', label: 'TDS Deducted' },
  { id: 'tdsDeposited', label: 'TDS Deposited' },
  { id: 'pendingAmt', label: 'Pending' },
  { id: 'dueDate', label: 'Due Date' },
  { id: 'status', label: 'Status' }
];

// ── Mock Data (replace with API) ───────────────────────────────────────
const MOCK_MONTHLY = [
  {
    id: 1,
    period: 'Apr 2024',
    month: 'April',
    fy: '2024-25',
    section: '194J',
    paymentAmt: 150000,
    tdsRate: '10%',
    tdsDeducted: 15000,
    tdsDeposited: 15000,
    pendingAmt: 0,
    dueDate: '07-05-2024',
    status: 'Deposited',
    challanNo: 'TDS-2024-04-001'
  },
  {
    id: 2,
    period: 'May 2024',
    month: 'May',
    fy: '2024-25',
    section: '194C',
    paymentAmt: 80000,
    tdsRate: '2%',
    tdsDeducted: 1600,
    tdsDeposited: 1600,
    pendingAmt: 0,
    dueDate: '07-06-2024',
    status: 'Deposited',
    challanNo: 'TDS-2024-05-002'
  },
  {
    id: 3,
    period: 'Jun 2024',
    month: 'June',
    fy: '2024-25',
    section: '194J',
    paymentAmt: 200000,
    tdsRate: '10%',
    tdsDeducted: 20000,
    tdsDeposited: 0,
    pendingAmt: 20000,
    dueDate: '07-07-2024',
    status: 'Pending',
    challanNo: ''
  },
  {
    id: 4,
    period: 'Jul 2024',
    month: 'July',
    fy: '2024-25',
    section: '194H',
    paymentAmt: 50000,
    tdsRate: '5%',
    tdsDeducted: 2500,
    tdsDeposited: 0,
    pendingAmt: 2500,
    dueDate: '07-08-2024',
    status: 'Pending',
    challanNo: ''
  }
];

const SECTIONS = ['194C', '194J', '194H', '194I', '194A'];
const FY_OPTIONS = ['2024-25', '2023-24', '2022-23'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const statusColor = (s) =>
  ({ Deposited: 'success', Pending: 'warning', Overdue: 'error' }[s] || 'warning');

// ── KPI Cards ─────────────────────────────────────────────────────────
const getKPIs = (rows) => ({
  deducted: rows.reduce((s, r) => s + r.tdsDeducted, 0),
  deposited: rows.reduce((s, r) => s + r.tdsDeposited, 0),
  pending: rows.reduce((s, r) => s + r.pendingAmt, 0),
  months: rows.length
});

export default function TDSMonthlySummary() {
  const [filterName, setFilterName] = useState('');
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [filterSection, setFilterSection] = useState(' ');
  const [filterFY, setFilterFY] = useState(' ');
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('period');

  // ── Export menu ──
  const [exportAnchor, setExportAnchor] = useState(null);

  // ── Challan dialog ──
  const [challanOpen, setChallanOpen] = useState(false);
  const [challanGenerated, setChallanGenerated] = useState(false);
  const [challanMonth, setChallanMonth] = useState('');
  const [challanFY, setChallanFY] = useState('2024-25');
  const [challanData, setChallanData] = useState(null);

  const handleRequestSort = (_, property) => {
    setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filtered = useMemo(() => {
    let data = [...MOCK_MONTHLY];
    if (filterName)
      data = data.filter(
        (r) =>
          r.period.toLowerCase().includes(filterName.toLowerCase()) ||
          r.section.toLowerCase().includes(filterName.toLowerCase())
      );
    if (filterSection !== ' ') data = data.filter((r) => r.section === filterSection);
    if (filterFY !== ' ') data = data.filter((r) => r.fy === filterFY);
    return lodashOrderBy(data, [orderBy], [order]);
  }, [filterName, filterSection, filterFY, order, orderBy]);

  const kpis = getKPIs(MOCK_MONTHLY);

  // ── Challan handlers ──
  const openChallan = () => {
    setChallanOpen(true);
    setChallanGenerated(false);
    setChallanMonth('');
    setChallanData(null);
  };
  const handleGenerateChallan = () => {
    // TODO: API call to generate TDS challan
    setChallanData({
      challanNo: `TDS-${challanFY.split('-')[0]}-${String(
        MONTHS.indexOf(challanMonth) + 1
      ).padStart(2, '0')}-${String(Math.floor(Math.random() * 900) + 100)}`,
      dueDate: `07-${String(MONTHS.indexOf(challanMonth) + 2).padStart(2, '0')}-${
        challanFY.split('-')[0]
      }`,
      amount: 17500,
      period: `${challanMonth} ${challanFY}`
    });
    setChallanGenerated(true);
  };
  const resetChallan = () => {
    setChallanOpen(false);
    setChallanGenerated(false);
    setChallanData(null);
  };

  const handleExport = (type) => {
    setExportAnchor(null);
    console.log('TDS Monthly export as', type);
  };

  return (
    <Box>
      {/* ── 4 KPI Cards ── */}
      <Grid container spacing={2} mb={3}>
        {[
          {
            label: 'TDS Deducted',
            value: kpis.deducted,
            icon: AssessmentIcon,
            iconBg: '#eff6ff',
            iconColor: '#1d4ed8',
            border: '#1d4ed8'
          },
          {
            label: 'TDS Deposited',
            value: kpis.deposited,
            icon: AccountBalanceIcon,
            iconBg: '#f0fdf4',
            iconColor: '#16a34a',
            border: '#16a34a'
          },
          {
            label: 'Pending',
            value: kpis.pending,
            icon: HourglassBottomIcon,
            iconBg: '#fff5f5',
            iconColor: '#dc2626',
            border: '#dc2626'
          },
          {
            label: 'Total Months',
            value: kpis.months,
            icon: PaymentsIcon,
            iconBg: '#f0f9ff',
            iconColor: '#0891b2',
            border: '#4fbbc8',
            isCount: true
          }
        ].map((kpi) => {
          const IconComp = kpi.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={kpi.label}>
              <Card
                sx={{
                  p: 2.5,
                  borderLeft: `4px solid ${kpi.border}`,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Box
                  sx={{
                    p: 1.2,
                    bgcolor: kpi.iconBg,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <IconComp sx={{ color: kpi.iconColor, fontSize: '1.4rem' }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 11, color: '#718096', fontWeight: 500, display: 'block' }}
                  >
                    {kpi.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, fontSize: 17, mt: 0.2, color: '#1e293b' }}
                  >
                    {kpi.isCount ? kpi.value : `₹ ${kpi.value.toLocaleString('en-IN')}`}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* ── Table Card ── */}
      <Card>
        {/* ── Collapsible Filter Bar ── */}
        {isOpenFilter && (
          <Box px={3} pt={2} pb={2} sx={{ borderBottom: '1px solid #eaedf1' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: 12 }}>TDS Section</InputLabel>
                  <Select
                    label="TDS Section"
                    value={filterSection}
                    onChange={(e) => setFilterSection(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value=" ">All Sections</MenuItem>
                    {SECTIONS.map((s) => (
                      <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: 12 }}>Financial Year</InputLabel>
                  <Select
                    label="Financial Year"
                    value={filterFY}
                    onChange={(e) => setFilterFY(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value=" ">All FY</MenuItem>
                    {FY_OPTIONS.map((fy) => (
                      <MenuItem key={fy} value={fy} sx={{ fontSize: 13 }}>
                        FY {fy}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs="auto">
                <Tooltip title="Reset Filters">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFilterSection(' ');
                      setFilterFY(' ');
                      setFilterName('');
                    }}
                  >
                    <FilterListOffIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ── Toolbar ── */}
        <RootStyle sx={{ minHeight: 45, borderBottom: '1px solid #eaedf1' }}>
          {/* LEFT — Export + Generate Challan */}
          <Box>
            {/* Export icon */}
            <Tooltip title="Export data" placement="top" arrow>
              <IconButton
                className="squareIconButton"
                onClick={(e) => setExportAnchor(e.currentTarget)}
              >
                <MdOutlineSystemUpdateAlt size={18} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              PaperProps={{
                sx: { borderRadius: 1, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
              }}
            >
              <MenuItem onClick={() => handleExport('pdf')} sx={{ fontSize: 13 }}>
                <PictureAsPdfIcon sx={{ fontSize: 16, mr: 1, color: '#dc2626' }} /> Export as PDF
              </MenuItem>
              <MenuItem onClick={() => handleExport('excel')} sx={{ fontSize: 13 }}>
                <GridOnIcon sx={{ fontSize: 16, mr: 1, color: '#16a34a' }} /> Export as Excel
              </MenuItem>
            </Menu>

            {/* Generate Challan */}
            <Tooltip title="Generate TDS Challan" placement="top" arrow>
              <IconButton className="squareIconButton" onClick={openChallan} sx={{ ml: 0.5 }}>
                <ReceiptLongIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* RIGHT — Search + Filter toggle */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <SearchStyle
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Search period, section…"
              startAdornment={
                <InputAdornment position="start">
                  <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
            />
            <Tooltip title="Filters">
              <IconButton onClick={() => setOpenFilter(!isOpenFilter)}>
                <Icon icon={roundFilterList} />
              </IconButton>
            </Tooltip>
          </Stack>
        </RootStyle>

        {/* ── Table ── */}
        <Scrollbar>
          <TableContainer sx={{ minWidth: 900, height: '50vh', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={filtered.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filtered.map((row) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell sx={{ fontWeight: 700, color: '#0d4689' }}>
                      {row.period}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={row.section}
                        size="small"
                        sx={{ fontSize: 10, bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>₹ {row.paymentAmt.toLocaleString()}</StyledTableCell>
                    <StyledTableCell>{row.tdsRate}</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 600 }}>
                      ₹ {row.tdsDeducted.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.tdsDeposited > 0 ? (
                        <span style={{ color: '#16a34a', fontWeight: 600 }}>
                          ₹ {row.tdsDeposited.toLocaleString()}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>–</span>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.pendingAmt > 0 ? (
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>
                          ₹ {row.pendingAmt.toLocaleString()}
                        </span>
                      ) : (
                        <span style={{ color: '#16a34a' }}>–</span>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>{row.dueDate}</StyledTableCell>
                    <StyledTableCell>
                      <Label variant="ghost" color={statusColor(row.status)}>
                        {row.status}
                      </Label>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              {filtered.length === 0 && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={9} sx={{ py: 4 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
        <SimplePagination page={page} setPage={setPage} hasNextPage={false} />
      </Card>

      {/* ── Generate Challan Dialog ── */}
      <Dialog open={challanOpen} onClose={resetChallan} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0d4689' }}>
            Generate TDS Challan
          </Typography>
          <IconButton size="small" onClick={resetChallan}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {!challanGenerated ? (
            <Box pt={1}>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5, fontSize: 13 }}>
                Select the period for which you want to generate the TDS challan.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    className="formLabel"
                    sx={{ fontSize: 12, color: '#374151', fontWeight: 600, mb: 0.5 }}
                  >
                    Month
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={challanMonth}
                    onChange={(e) => setChallanMonth(e.target.value)}
                  >
                    <MenuItem value="">Select Month</MenuItem>
                    {MONTHS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    className="formLabel"
                    sx={{ fontSize: 12, color: '#374151', fontWeight: 600, mb: 0.5 }}
                  >
                    Financial Year
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    value={challanFY}
                    onChange={(e) => setChallanFY(e.target.value)}
                  >
                    {FY_OPTIONS.map((fy) => (
                      <MenuItem key={fy} value={fy}>
                        {fy}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box pt={1}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2.5,
                    py: 0.8,
                    bgcolor: '#dcfce7',
                    borderRadius: '999px'
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#16a34a' }} />
                  <Typography sx={{ fontSize: 13, color: '#16a34a', fontWeight: 700 }}>
                    Challan Generated Successfully
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ border: '1px solid #eaedf1', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ bgcolor: '#0d4689', px: 3, py: 1.5 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                    TDS Challan for {challanData?.period}
                  </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#64748b', fontSize: 11, display: 'block' }}
                      >
                        Challan No.
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0d4689', mt: 0.3 }}>
                        {challanData?.challanNo}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#64748b', fontSize: 11, display: 'block' }}
                      >
                        Due Date
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#dc2626', mt: 0.3 }}>
                        {challanData?.dueDate}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 0.5 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#64748b', fontSize: 11, display: 'block' }}
                      >
                        Amount
                      </Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: 22, color: '#0d4689', mt: 0.3 }}>
                        ₹ {challanData?.amount?.toLocaleString('en-IN')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          {!challanGenerated ? (
            <>
              <Button onClick={resetChallan} sx={{ textTransform: 'none', color: '#64748b' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                className="mainBtn"
                disabled={!challanMonth}
                onClick={handleGenerateChallan}
                sx={{ textTransform: 'none' }}
              >
                Generate Challan
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={resetChallan}
                startIcon={<CloseIcon />}
                sx={{ textTransform: 'none', color: '#64748b' }}
              >
                Close
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{ textTransform: 'none' }}
              >
                Print
              </Button>
              <Button
                variant="contained"
                className="mainBtn"
                startIcon={<DownloadIcon />}
                onClick={() => console.log('TODO: Download TDS Challan PDF', challanData)}
                sx={{ textTransform: 'none' }}
              >
                Download PDF
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
